import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { authAPI, vendorAPI } from '../services/api';

// Bump this when the DPDP Notice PDF is revised (e.g. after legal review).
// Every registration records which version the person actually agreed to.
const DPDP_VERSION = '0.3';

const inputStyle = {
  width:'100%',
  background:'var(--surface2)',
  border:'1px solid var(--border2)',
  color:'var(--text)',
  padding:'11px 14px',
  borderRadius:'8px',
  fontSize:'14px',
  outline:'none',
};

const labelStyle = {
  display:'block',
  fontSize:'11px',
  fontFamily:'DM Mono,monospace',
  color:'var(--text3)',
  letterSpacing:'1px',
  textTransform:'uppercase',
  marginBottom:'6px',
};

const VendorRegister = () => {
  const navigate = useNavigate();
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [agreedToDpdp, setAgreedToDpdp] = useState(false);
  const [form, setForm] = useState({
    business_name:'',
    owner_name:'',
    email:'',
    phone:'',
    password:'',
    city:'',
    state:'',
    pincode:'',
    business_category_id:'',
    business_type:'',
    gst_number:'',
    address:'',
    num_engineers:1,
  });

  useEffect(() => {
    vendorAPI.getCategories()
      .then(res => setCategories(res.data.data))
      .catch(() => setCategories([]));
  }, []);

  const update = (key, value) =>
    setForm(prev => ({ ...prev, [key]: value }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (!form.business_name || !form.owner_name ||
        !form.email || !form.phone ||
        !form.password || !form.city ||
        !form.state || !form.pincode) {
      setError('Please fill all required fields');
      return;
    }
    if (form.password.length < 8) {
      setError('Password must be at least 8 characters');
      return;
    }
    if (!agreedToDpdp) {
      setError('Please read and agree to the Data Protection & Privacy Notice to continue');
      return;
    }

    setLoading(true);
    try {
      await authAPI.vendorRegister({ ...form, dpdp_version: DPDP_VERSION });
      navigate('/verify-otp', {
        state: { email: form.email }
      });
    } catch (err) {
      setError(
        err.response?.data?.message
        || 'Registration failed. Please try again.'
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <div style={{
      minHeight:'100vh',
      background:'var(--bg)',
      display:'flex',
      alignItems:'center',
      justifyContent:'center',
      padding:'20px',
    }}>
      <div style={{ width:'100%', maxWidth:'560px' }}>
        <div style={{ textAlign:'center', marginBottom:'32px' }}>
          <img
            src="/logo512.png"
            alt="IRONWALL Service Bridge"
            style={{
              width:'72px', height:'72px',
              borderRadius:'16px',
              margin:'0 auto 16px',
              display:'block',
            }}
          />
          <h1 style={{
            fontFamily:'Syne,sans-serif',
            fontSize:'26px', fontWeight:'800',
            color:'var(--text)',
            marginBottom:'6px',
          }}>
            IRONWALL
          </h1>
          <p style={{
            fontSize:'13px',
            color:'var(--text3)',
            fontFamily:'DM Mono,monospace',
            letterSpacing:'1px',
          }}>
            VENDOR REGISTRATION
          </p>
          <a
            href="/Ironwall_Servicebridge_Guide.pdf"
            target="_blank"
            rel="noopener noreferrer"
            style={{
              display:'inline-block',
              marginTop:'14px',
              fontSize:'13px',
              color:'var(--accent)',
              textDecoration:'underline',
              textUnderlineOffset:'2px',
            }}
          >
            📄 New here? Download the Getting Started Guide
          </a>
        </div>

        <div style={{
          background:'var(--surface)',
          border:'1px solid var(--border)',
          borderRadius:'16px',
          padding:'32px',
        }}>
          <form onSubmit={handleSubmit}>
            <div style={{
              display:'grid',
              gridTemplateColumns:'1fr 1fr',
              gap:'16px',
              marginBottom:'16px',
            }}>
              <div>
                <label style={labelStyle}>Business Name *</label>
                <input style={inputStyle}
                  value={form.business_name}
                  onChange={e => update('business_name', e.target.value)}
                />
              </div>
              <div>
                <label style={labelStyle}>Owner Name *</label>
                <input style={inputStyle}
                  value={form.owner_name}
                  onChange={e => update('owner_name', e.target.value)}
                />
              </div>
            </div>

            <div style={{
              display:'grid',
              gridTemplateColumns:'1fr 1fr',
              gap:'16px',
              marginBottom:'16px',
            }}>
              <div>
                <label style={labelStyle}>Email Address *</label>
                <input type="email" style={inputStyle}
                  value={form.email}
                  onChange={e => update('email', e.target.value)}
                />
              </div>
              <div>
                <label style={labelStyle}>Phone *</label>
                <input style={inputStyle}
                  value={form.phone}
                  onChange={e => update('phone', e.target.value)}
                />
              </div>
            </div>

            <div style={{ marginBottom:'16px' }}>
              <label style={labelStyle}>Password *</label>
              <input type="password" style={inputStyle}
                placeholder="At least 8 characters"
                value={form.password}
                onChange={e => update('password', e.target.value)}
              />
            </div>

            <div style={{
              display:'grid',
              gridTemplateColumns:'1fr 1fr 1fr',
              gap:'16px',
              marginBottom:'16px',
            }}>
              <div>
                <label style={labelStyle}>City *</label>
                <input style={inputStyle}
                  value={form.city}
                  onChange={e => update('city', e.target.value)}
                />
              </div>
              <div>
                <label style={labelStyle}>State *</label>
                <input style={inputStyle}
                  value={form.state}
                  onChange={e => update('state', e.target.value)}
                />
              </div>
              <div>
                <label style={labelStyle}>Pincode *</label>
                <input style={inputStyle}
                  value={form.pincode}
                  onChange={e => update('pincode', e.target.value)}
                />
              </div>
            </div>

            <div style={{ marginBottom:'16px' }}>
              <label style={labelStyle}>Address</label>
              <input style={inputStyle}
                value={form.address}
                onChange={e => update('address', e.target.value)}
              />
            </div>

            <div style={{
              display:'grid',
              gridTemplateColumns:'1fr 1fr',
              gap:'16px',
              marginBottom:'16px',
            }}>
              <div>
                <label style={labelStyle}>Business Category</label>
                <select style={inputStyle}
                  value={form.business_category_id}
                  onChange={e => update('business_category_id', e.target.value)}
                >
                  <option value="">— Select —</option>
                  {categories.map(c => (
                    <option key={c.id} value={c.id}>{c.name}</option>
                  ))}
                </select>
              </div>
              <div>
                <label style={labelStyle}>No. of Engineers</label>
                <input type="number" min="1" style={inputStyle}
                  value={form.num_engineers}
                  onChange={e => update('num_engineers', e.target.value)}
                />
              </div>
            </div>

            <div style={{ marginBottom:'24px' }}>
              <label style={labelStyle}>GST Number (optional)</label>
              <input style={inputStyle}
                value={form.gst_number}
                onChange={e => update('gst_number', e.target.value)}
              />
            </div>

            <div style={{
              display:'flex',
              alignItems:'flex-start',
              gap:'10px',
              marginBottom:'20px',
              padding:'12px 14px',
              background:'var(--surface2)',
              borderRadius:'8px',
              border:'1px solid var(--border2)',
            }}>
              <input
                type="checkbox"
                id="dpdp-agree"
                checked={agreedToDpdp}
                onChange={e => setAgreedToDpdp(e.target.checked)}
                style={{ marginTop:'3px', cursor:'pointer', flexShrink:0 }}
              />
              <label htmlFor="dpdp-agree" style={{
                fontSize:'13px',
                color:'var(--text2)',
                cursor:'pointer',
                lineHeight:1.4,
              }}>
                I have read and agree to the{' '}
                <a
                  href="/DPDP_Notice.pdf"
                  target="_blank"
                  rel="noopener noreferrer"
                  onClick={e => e.stopPropagation()}
                  style={{ color:'var(--accent)', textDecoration:'underline' }}
                >
                  Data Protection &amp; Privacy Notice
                </a>
              </label>
            </div>

            {error && (
              <div style={{
                background:'rgba(255,77,109,0.1)',
                border:'1px solid rgba(255,77,109,0.3)',
                color:'var(--red)',
                padding:'12px 14px',
                borderRadius:'8px',
                fontSize:'13px',
                marginBottom:'16px',
              }}>
                {error}
              </div>
            )}

            <button type="submit" disabled={loading}
              style={{
                width:'100%',
                background: loading ? 'var(--surface3)' : 'var(--accent)',
                color: loading ? 'var(--text3)' : 'var(--bg)',
                border:'none',
                padding:'12px',
                borderRadius:'8px',
                fontSize:'14px',
                fontWeight:'700',
                cursor: loading ? 'not-allowed' : 'pointer',
              }}
            >
              {loading ? 'Creating account...' : 'Create Account →'}
            </button>
          </form>

          <p style={{
            textAlign:'center',
            marginTop:'20px',
            fontSize:'13px',
            color:'var(--text3)',
          }}>
            Already registered?{' '}
            <span onClick={() => navigate('/login')}
              style={{ color:'var(--accent)', cursor:'pointer' }}>
              Sign in
            </span>
          </p>
        </div>
      </div>
    </div>
  );
};

export default VendorRegister;